/**
 * Schema Version Detector
 *
 * Pure async helper that determines whether an OnboardingVersion is supported by Web Placements.
 *
 * Web Placements are V2-only. The detector primarily uses the version document
 * and never probes fallback subcollections, so list rendering stays cheap. For
 * migrated V2 data that predates nested `screensGraph.schemaVersion`, it falls
 * back to the parent onboarding `screenGraphType`.
 *
 * Detection algorithm:
 *  1. Read `version.screensGraph.schemaVersion`.
 *  2. If it starts with `'2.'`, return `'v2'`.
 *  3. If it is explicitly non-2.x, return `'unsupported'`.
 *  4. If it is missing, read parent onboarding `screenGraphType`.
 *  5. If the parent is V2, return `'v2'`; otherwise return `'unsupported'`.
 *  6. If required documents cannot be read, return `'error'`.
 *
 * @module webplacements/services
 */

import { OnboardingService } from '@modules/firebase-core/services/OnboardingService';
import {
  getExplicitSchemaVersion,
  inferV2SchemaVersionFromParent,
  isV2SchemaVersion,
} from '@modules/webplacements/services/schemaVersionSupport';

export type SchemaVersionResult = 'v2' | 'unsupported' | 'error';

/**
 * Detect whether the given onboarding version can be published by Web Placements.
 *
 * Never throws — wraps every Firestore call. Returns `'error'` for any
 * unrecoverable read failure (caller can render an "unknown" state).
 */
export async function detectSchemaVersion(
  projectId: string,
  funnelId: string,
  versionId: string
): Promise<SchemaVersionResult> {
  if (!projectId || !funnelId || !versionId) {
    return 'error';
  }

  const onboardingService = new OnboardingService();

  let schemaVersion = '';
  try {
    const version = await onboardingService.getVersion(projectId, funnelId, versionId);
    if (!version) {
      return 'error';
    }
    schemaVersion = getExplicitSchemaVersion(version);
  } catch (error) {
    console.error('[detectSchemaVersion] getVersion failed', { projectId, funnelId, versionId, error });
    return 'error';
  }

  if (isV2SchemaVersion(schemaVersion)) {
    return 'v2';
  }

  if (schemaVersion) {
    return 'unsupported';
  }

  try {
    const onboarding = await onboardingService.getOnboarding(projectId, funnelId);
    if (inferV2SchemaVersionFromParent(onboarding)) {
      return 'v2';
    }
  } catch (error) {
    console.error('[detectSchemaVersion] getOnboarding failed', { projectId, funnelId, versionId, error });
    return 'error';
  }

  return 'unsupported';
}
